import { useState, useEffect } from 'react';
import { rmmAPI, hrAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ShieldOff, Trash2, UserCog, Power, RefreshCcw, Waves, Ticket, Eraser } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import { fmtWhen, fmtUptime, SEVERITY_STYLES, ALERT_TYPE_LABELS } from './rmmUtils';
import { RemoteTools } from './RemoteTools';

export const DeviceDetail = ({ deviceId, isAdmin, onClose, onChanged }) => {
  const [device, setDevice] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [techs, setTechs] = useState([]);
  const [commands, setCommands] = useState([]);
  const [serviceName, setServiceName] = useState('');
  const [killPid, setKillPid] = useState('');
  const [scriptId, setScriptId] = useState('');
  const [scripts, setScripts] = useState([]);
  const [effPolicy, setEffPolicy] = useState(null);
  const [queueing, setQueueing] = useState(false);

  const loadCommands = () => rmmAPI.listCommands(deviceId).then((res) => setCommands(res.data)).catch(() => {});

  useEffect(() => {
    rmmAPI.getDevice(deviceId).then((res) => setDevice(res.data)).catch(() => onClose());
    rmmAPI.deviceMetrics(deviceId, 24).then((res) => setMetrics(res.data)).catch(() => {});
    loadCommands();
    rmmAPI.effectivePolicy(deviceId).then((r) => setEffPolicy(r.data)).catch(() => {});
    if (isAdmin) {
      hrAPI.listEmployees().then((res) => setTechs(res.data)).catch(() => {});
      rmmAPI.listScripts().then((r) => setScripts(r.data.filter((s) => s.status === 'approved'))).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, onClose, isAdmin]);

  const queue = async (type, params = {}) => {
    const labels = { reboot: 'reboot this device', restart_service: `restart service "${params.service_name}"`, flush_dns: 'flush the DNS cache', list_processes: 'list running processes', list_services: 'list Windows services', kill_process: `terminate process PID ${params.pid}`, run_script: 'run the selected approved script', remote_uninstall: 'uninstall the RMM agent from this device (it will stop reporting)' };
    if (!confirm(`Queue remote action: ${labels[type] || type}? The agent executes it on its next heartbeat.`)) return;
    setQueueing(true);
    try {
      await rmmAPI.queueCommand(deviceId, type, params);
      toast.success('Command queued — the agent will run it on its next heartbeat');
      setServiceName('');
      loadCommands();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to queue command');
    } finally {
      setQueueing(false);
    }
  };

  const assign = async (techId) => {
    try {
      const res = await rmmAPI.assignTechnician(deviceId, techId === 'none' ? null : techId);
      setDevice((d) => ({ ...d, ...res.data }));
      toast.success(techId === 'none' ? 'Technician unassigned' : 'Technician assigned');
      onChanged();
    } catch { toast.error('Failed to assign technician'); }
  };

  const chartData = metrics.map((m) => ({
    t: new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    CPU: m.cpu_percent, RAM: m.ram_percent, Disk: m.disk_percent,
  }));

  const revoke = async () => {
    if (!confirm('Revoke this device\'s agent token? It will stop reporting until re-enrolled.')) return;
    try {
      await rmmAPI.revokeDeviceToken(deviceId);
      toast.success('Agent token revoked');
      onChanged(); onClose();
    } catch { toast.error('Failed to revoke token'); }
  };

  const remove = async () => {
    if (!confirm('Remove this device and resolve its alerts?')) return;
    try {
      await rmmAPI.deleteDevice(deviceId);
      toast.success('Device removed');
      onChanged(); onClose();
    } catch { toast.error('Failed to remove device'); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="rmm-device-detail">
        {!device ? (
          <>
            <DialogHeader><DialogTitle>Device details</DialogTitle></DialogHeader>
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                {device.hostname}
                {device.token_revoked
                  ? <Badge className="bg-slate-200 text-slate-600">revoked</Badge>
                  : <Badge className={device.status === 'online' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{device.status}</Badge>}
              </DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              {[
                ['Customer', device.customer_name || '—'],
                ['Site', device.site_name || '—'],
                ['OS', device.os_version || device.os || '—'],
                ['Agent', `v${device.agent_version || '?'}${device.device_type ? ` · ${device.device_type}` : ''}`],
                ['Manufacturer / Model', [device.manufacturer, device.model].filter(Boolean).join(' ') || '—'],
                ['Serial Number', device.serial_number || '—'],
                ['BIOS', device.bios_version || '—'],
                ['CPU Model', device.cpu_model ? `${device.cpu_model}${device.cpu_cores ? ` (${device.cpu_cores} cores)` : ''}` : '—'],
                ['Total RAM', device.ram_total_gb != null ? `${device.ram_total_gb} GB` : '—'],
                ['MAC Address', device.mac_address || '—'],
                ['Logged-in User', device.logged_in_user || '—'],
                ['IP Address', device.ip_address || '—'],
                ['Uptime', fmtUptime(device.uptime_seconds)],
                ['Last Seen', fmtWhen(device.last_seen)],
                ['CPU', device.cpu_percent != null ? `${Math.round(device.cpu_percent)}%` : '—'],
                ['RAM', device.ram_percent != null ? `${Math.round(device.ram_percent)}%` : '—'],
                ['Disk Used', device.disk_percent != null ? `${Math.round(device.disk_percent)}%` : '—'],
                ['Disk Free', device.disk_free_gb != null ? `${Number(device.disk_free_gb).toFixed(1)} GB / ${Number(device.disk_total_gb || 0).toFixed(0)} GB` : '—'],
                ['Assigned Tech', device.assigned_technician_name || 'Unassigned'],
                ['Enrolled', fmtWhen(device.enrolled_at)],
                ['Policy', effPolicy ? `${effPolicy.name} (${effPolicy.scope?.level})` : '—'],
                ['Software / Updates', `${(device.software || []).length} apps · ${device.windows_updates?.pending_count ?? '—'} pending${device.windows_updates?.reboot_required ? ' · reboot req.' : ''}`],
              ].map(([k, v]) => (
                <div key={k} className="p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                  <p className="text-[11px] text-slate-400">{k}</p>
                  <p className="text-slate-800 font-medium truncate" title={String(v)}>{v}</p>
                </div>
              ))}
            </div>

            {(device.drives || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {device.drives.map((dr) => (
                  <Badge key={dr.letter} variant="secondary" className="font-mono">
                    {dr.letter} {Number(dr.free_gb).toFixed(0)} GB free / {Number(dr.total_gb).toFixed(0)} GB
                  </Badge>
                ))}
              </div>
            )}

            {isAdmin && (
              <div className="flex items-center gap-2">
                <UserCog className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-600">Assigned technician:</span>
                <Select value={device.assigned_technician_id || 'none'} onValueChange={assign}>
                  <SelectTrigger className="w-56 h-8 bg-white border-slate-200" data-testid="rmm-assign-tech-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {(device.open_alerts || []).length > 0 && (
              <div className="space-y-1.5">
                <p className="text-sm font-semibold text-slate-700">Open Alerts</p>
                {device.open_alerts.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 text-sm p-2 rounded-lg border border-slate-200">
                    <Badge className={SEVERITY_STYLES[a.severity]}>{a.severity}</Badge>
                    <span className="text-slate-700">{ALERT_TYPE_LABELS[a.type] || a.type}</span>
                    <span className="text-xs text-slate-400 ml-auto">{fmtWhen(a.created_at)}</span>
                  </div>
                ))}
              </div>
            )}

            {isAdmin && !device.token_revoked && (
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <p className="text-sm font-semibold text-slate-700">Remote Actions <span className="text-xs font-normal text-slate-400">— executed by the agent on its next heartbeat, fully audited</span></p>
                <div className="flex flex-wrap gap-2 items-center">
                  <Button size="sm" variant="outline" className="rounded-full" disabled={queueing} onClick={() => queue('reboot')} data-testid="rmm-cmd-reboot-btn">
                    <Power className="w-4 h-4 mr-1 text-red-500" /> Reboot
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" disabled={queueing} onClick={() => queue('flush_dns')} data-testid="rmm-cmd-flushdns-btn">
                    <Waves className="w-4 h-4 mr-1 text-cyan-500" /> Flush DNS
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" disabled={queueing} onClick={() => queue('list_processes')} data-testid="rmm-cmd-listproc-btn">Processes</Button>
                  <Button size="sm" variant="outline" className="rounded-full" disabled={queueing} onClick={() => queue('list_services')} data-testid="rmm-cmd-listsvc-btn">Services</Button>
                  <div className="flex gap-1 items-center">
                    <Input placeholder="Service name (e.g. Spooler)" value={serviceName} onChange={(e) => setServiceName(e.target.value)} className="h-8 w-52 bg-white border-slate-200" data-testid="rmm-cmd-service-input" />
                    <Button size="sm" variant="outline" className="rounded-full" disabled={queueing || !serviceName.trim()} onClick={() => queue('restart_service', { service_name: serviceName.trim() })} data-testid="rmm-cmd-restartsvc-btn">
                      <RefreshCcw className="w-4 h-4 mr-1 text-amber-500" /> Restart Service
                    </Button>
                  </div>
                  <div className="flex gap-1 items-center">
                    <Input placeholder="PID" value={killPid} onChange={(e) => setKillPid(e.target.value)} className="h-8 w-20 bg-white border-slate-200" data-testid="rmm-cmd-pid-input" />
                    <Button size="sm" variant="outline" className="rounded-full text-red-500" disabled={queueing || !/^\d+$/.test(killPid)} onClick={() => queue('kill_process', { pid: killPid })} data-testid="rmm-cmd-killproc-btn">Kill Process</Button>
                  </div>
                  {scripts.length > 0 && (
                    <div className="flex gap-1 items-center">
                      <Select value={scriptId} onValueChange={setScriptId}>
                        <SelectTrigger className="h-8 w-48 bg-white border-slate-200" data-testid="rmm-run-script-select"><SelectValue placeholder="Approved script…" /></SelectTrigger>
                        <SelectContent>{scripts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} (v{s.version})</SelectItem>)}</SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" className="rounded-full" disabled={queueing || !scriptId} onClick={() => queue('run_script', { script_id: scriptId })} data-testid="rmm-run-script-btn">Run Script</Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {commands.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-sm font-semibold text-slate-700">Command History</p>
                <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="rmm-command-history">
                  {commands.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 text-xs p-2 rounded-lg border border-slate-200">
                      <Badge className={
                        c.status === 'completed' ? 'bg-emerald-100 text-emerald-700'
                          : c.status === 'failed' ? 'bg-red-100 text-red-700'
                          : c.status === 'expired' ? 'bg-slate-200 text-slate-600'
                          : 'bg-amber-100 text-amber-700'}>{c.status}</Badge>
                      <span className="font-medium text-slate-700">{c.type}</span>
                      {c.params?.service_name && <span className="font-mono text-slate-500">{c.params.service_name}</span>}
                      <span className="text-slate-400 truncate">{c.output || `queued by ${c.queued_by_name}`}</span>
                      <span className="text-slate-400 ml-auto shrink-0">{fmtWhen(c.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">Metrics — last 24h</p>
              {chartData.length === 0 ? (
                <p className="text-sm text-slate-400">No metric history yet.</p>
              ) : (
                <div className="h-56" data-testid="rmm-metrics-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="t" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="CPU" stroke="#06b6d4" dot={false} strokeWidth={2} />
                      <Line type="monotone" dataKey="RAM" stroke="#8b5cf6" dot={false} strokeWidth={2} />
                      <Line type="monotone" dataKey="Disk" stroke="#f59e0b" dot={false} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {(device.managed_via || 'agent') !== 'network' && <RemoteTools device={device} isAdmin={isAdmin} />}

            {isAdmin && (
              <div className="flex gap-2 pt-2 border-t border-slate-100 flex-wrap">
                {!device.token_revoked && (device.managed_via || 'agent') !== 'network' && (
                  <Button variant="outline" className="rounded-full text-orange-600 border-orange-200 hover:bg-orange-50" disabled={queueing} onClick={() => queue('remote_uninstall')} data-testid="rmm-uninstall-agent-btn">
                    <Eraser className="w-4 h-4 mr-1" /> Uninstall Agent
                  </Button>
                )}
                {!device.token_revoked && (
                  <Button variant="outline" className="rounded-full text-amber-600 border-amber-200 hover:bg-amber-50" onClick={revoke} data-testid="rmm-revoke-token-btn">
                    <ShieldOff className="w-4 h-4 mr-1" /> Revoke Agent Token
                  </Button>
                )}
                <Button variant="outline" className="rounded-full text-red-500 border-red-200 hover:bg-red-50" onClick={remove} data-testid="rmm-delete-device-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Remove Device
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
